import { KIND_AR } from './config';
import type { PlanFloor, Snapshot, SnapshotUnit } from './data';
import { STAGES } from './scene/building';
import { BUILD_MAX, type PresetName } from './scene/stage';

export interface UiHandlers {
  preset(name: PresetName): void;
  explode(value: number): void;
  isolate(floorKey: string | null): void;
  xray(on: boolean): void;
  build(value: number): void;
  play(): void;
  pause(): void;
  closeCard(): void;
  enquire(unit: SnapshotUnit): void;
  /** A unit chosen from the list rather than in the model. */
  focus(unit: SnapshotUnit): void;
}

const fmt = new Intl.NumberFormat('en-US');
const sqm = (n: number) => `${fmt.format(n)} م²`;
const two = (n: number) => String(n).padStart(2, '0');

function terraceText(unit: SnapshotUnit) {
  if (unit.terraceSqm === null) return 'مرسوم، بلا مساحة مطبوعة';
  if (unit.terraceSqm === 0) return 'لا يوجد';
  return sqm(unit.terraceSqm);
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  props: Partial<HTMLElementTagNameMap[K]> & { dataset?: Record<string, string> } = {},
  ...children: (Node | string)[]
) {
  const node = document.createElement(tag);
  const { dataset, ...rest } = props;
  Object.assign(node, rest);
  if (dataset) Object.assign(node.dataset, dataset);
  node.append(...children);
  return node;
}

/**
 * The interface over the model: dark glass in the manner of the Architectural
 * X-ray demo Ghassan pointed at, in the brand's graphite with mint as the one
 * accent. The text is Arabic throughout; Latin appears only in plan codes and
 * brand names, which are what the signs say.
 */
export class Ui {
  private readonly presetButtons = new Map<PresetName, HTMLButtonElement>();
  private readonly floorButtons = new Map<string | null, HTMLButtonElement>();
  private readonly xrayButton: HTMLButtonElement;
  private readonly slider: HTMLInputElement;
  private readonly timeline: HTMLInputElement;
  private readonly playButton: HTMLButtonElement;
  private readonly percent: HTMLElement;
  private readonly stageMarks: HTMLElement[] = [];
  private readonly heading: {
    eyebrow: HTMLElement;
    title: HTMLElement;
    lede: HTMLElement;
    accent: HTMLElement;
  };
  private readonly card: HTMLElement;
  private readonly list: HTMLElement;
  private readonly floorLabels: Map<string, string>;
  private readonly lede: string;

  constructor(
    root: HTMLElement,
    floors: PlanFloor[],
    private readonly snapshot: Snapshot,
    private readonly on: UiHandlers,
  ) {
    this.floorLabels = new Map(floors.map((f) => [f.key, f.labelAr]));
    const available = snapshot.units.filter((u) => u.status === 'available').length;
    const leased = snapshot.units.length - available;
    this.lede = `${floors.length} أدوار · ${snapshot.units.length} مساحة للمطاعم والمقاهي على الواجهة البحرية`;

    // ── The heading: the property, or the construction stage being shown ──
    this.heading = {
      eyebrow: el('p', { className: 'eyebrow', textContent: 'الخبر · مجمع مطل البحر' }),
      title: el('h1', { textContent: 'مطل البحر' }),
      lede: el('p', { className: 'lede', textContent: this.lede }),
      accent: el('p', { className: 'accent', textContent: `${available} مساحات متاحة الآن` }),
    };
    const header = el(
      'header',
      { className: 'masthead' },
      this.heading.eyebrow,
      this.heading.title,
      this.heading.lede,
      this.heading.accent,
      ...(snapshot.fixture
        ? [
            el('p', {
              className: 'fixture',
              textContent: 'نموذج أولي — الإشغال تجريبي والتفاصيل تقديرية',
            }),
          ]
        : []),
    );

    // ── Views ──────────────────────────────────────────────────────────────
    const presets = el('nav', { className: 'pills', ariaLabel: 'زوايا العرض' });
    const presetNames: [PresetName, string][] = [
      ['hero', 'منظور'],
      ['explode', 'تفكيك'],
      ['dusk', 'ليل'],
    ];
    for (const [name, label] of presetNames) {
      const button = el('button', { type: 'button', className: 'pill' }, el('i'), label);
      button.addEventListener('click', () => this.on.preset(name));
      this.presetButtons.set(name, button);
      presets.append(button);
    }
    this.xrayButton = el(
      'button',
      { type: 'button', className: 'pill pill--xray' },
      el('i'),
      'شفّاف',
    );
    this.xrayButton.addEventListener('click', () => {
      const on = this.xrayButton.getAttribute('aria-pressed') !== 'true';
      this.setXray(on);
      this.on.xray(on);
    });
    presets.append(this.xrayButton);

    // ── Construction timeline ──────────────────────────────────────────────
    this.timeline = el('input', {
      type: 'range',
      min: '0',
      max: String(BUILD_MAX * 100),
      step: '1',
      value: String(BUILD_MAX * 100),
      ariaLabel: 'مراحل البناء',
    });
    this.timeline.addEventListener('input', () => {
      this.on.build(Number(this.timeline.value) / 100);
      this.setBuild(Number(this.timeline.value) / 100, false);
    });
    this.playButton = el('button', {
      type: 'button',
      className: 'play',
      ariaLabel: 'تشغيل مراحل البناء',
    });
    this.playButton.addEventListener('click', () => {
      if (this.playButton.classList.contains('is-playing')) this.on.pause();
      else this.on.play();
    });
    this.percent = el('span', { className: 'percent', textContent: '100%' });
    const marks = el('div', { className: 'marks' });
    STAGES.forEach((s, i) => {
      const mark = el(
        'button',
        { type: 'button', className: 'mark' },
        el('span', { textContent: two(i + 1) }),
        s.labelAr,
      );
      // A stage's mark shows it complete: the slider stops at its end.
      mark.addEventListener('click', () => {
        this.on.build(i + 1);
        this.setBuild(i + 1, false);
      });
      this.stageMarks.push(mark);
      marks.append(mark);
    });
    const timeline = el(
      'section',
      { className: 'timeline glass', ariaLabel: 'مراحل البناء' },
      marks,
      el('div', { className: 'track' }, this.playButton, this.timeline, this.percent),
    );

    // ── Floors, explode, legend ────────────────────────────────────────────
    this.slider = el('input', {
      type: 'range',
      min: '0',
      max: '1',
      step: '0.01',
      value: '0',
      id: 'explode',
    });
    this.slider.addEventListener('input', () => this.on.explode(Number(this.slider.value)));

    const floorRow = el('div', { className: 'floors', role: 'group', ariaLabel: 'الأدوار' });
    const options: [string | null, string][] = [
      [null, 'كل الأدوار'],
      ...[...floors]
        .sort((a, b) => a.order - b.order)
        .map((f): [string, string] => [f.key, f.labelAr.replace('الدور ', '')]),
    ];
    for (const [key, label] of options) {
      const button = el('button', { type: 'button', textContent: label, className: 'chip' });
      button.addEventListener('click', () => this.on.isolate(key));
      this.floorButtons.set(key, button);
      floorRow.append(button);
    }

    const dock = el(
      'section',
      { className: 'dock glass', ariaLabel: 'التحكم' },
      floorRow,
      el(
        'label',
        { className: 'slider', htmlFor: 'explode' },
        el('span', { textContent: 'تفكيك' }),
        this.slider,
      ),
      el(
        'div',
        { className: 'legend' },
        el('span', { className: 'key is-available' }, el('i'), `متاح ${available}`),
        el('span', { className: 'key is-leased' }, el('i'), `مؤجَّر ${leased}`),
        // Phones have no room for the list beside the model; this opens it.
        el('button', {
          type: 'button',
          className: 'list-toggle',
          textContent: 'قائمة المساحات',
          onclick: () => this.openList(true),
        }),
      ),
    );

    this.card = el('aside', {
      className: 'card glass',
      hidden: true,
      role: 'dialog',
      ariaLive: 'polite',
      ariaLabel: 'بطاقة المساحة',
    });

    const note = el('p', {
      className: 'footnote',
      textContent:
        'المساحات كما طُبعت على المخطط التسويقي وليست معتمدة. الارتفاعات والمواد والمحيط ومراحل البناء تقديرية للتوضيح.',
    });

    this.list = this.buildList(snapshot.units);

    // The list follows the card in the DOM so CSS can hide it while a card is open.
    root.append(header, presets, timeline, dock, this.card, this.list, note);

    window.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.on.closeCard();
    });
  }

  /**
   * Every space, available first, so a prospect can choose without hunting
   * in the model. On a wide screen it sits where the card does and gives way
   * to it; on a phone it is a sheet opened from the dock.
   */
  private buildList(units: SnapshotUnit[]) {
    const order = new Map([...this.floorLabels.keys()].map((k, i) => [k, i]));
    const sorted = [...units].sort(
      (a, b) =>
        (order.get(a.floor) ?? 0) - (order.get(b.floor) ?? 0) || a.code.localeCompare(b.code),
    );
    const row = (unit: SnapshotUnit) => {
      const available = unit.status === 'available';
      const button = el(
        'button',
        { type: 'button', className: `unit-row ${available ? 'is-available' : 'is-leased'}` },
        el('i'),
        el('b', { textContent: unit.code, dir: 'ltr' }),
        el('span', {
          textContent: available
            ? (this.floorLabels.get(unit.floor) ?? unit.floor)
            : (unit.brand ?? 'مؤجَّر'),
          dir: available ? 'rtl' : 'auto',
        }),
        el('span', {
          className: 'area',
          textContent: unit.areaSqm === null ? '—' : sqm(unit.areaSqm),
        }),
      );
      button.addEventListener('click', () => {
        this.openList(false);
        this.on.focus(unit);
      });
      return button;
    };
    const free = sorted.filter((u) => u.status === 'available');
    const taken = sorted.filter((u) => u.status !== 'available');

    const close = el('button', {
      type: 'button',
      className: 'close',
      ariaLabel: 'إغلاق',
      textContent: '×',
      onclick: () => this.openList(false),
    });
    return el(
      'aside',
      { className: 'units glass', ariaLabel: 'قائمة المساحات' },
      close,
      el('h2', {}, el('i'), `المساحات المتاحة · ${free.length}`),
      el('div', { className: 'unit-rows' }, ...free.map(row)),
      el(
        'details',
        {},
        el('summary', { textContent: `المؤجَّرة · ${taken.length}` }),
        el('div', { className: 'unit-rows' }, ...taken.map(row)),
      ),
    );
  }

  private openList(open: boolean) {
    this.list.classList.toggle('is-open', open);
  }

  setPreset(name: PresetName | null) {
    for (const [key, button] of this.presetButtons) {
      button.setAttribute('aria-pressed', String(key === name));
    }
    if (name) this.setXray(false);
  }

  setXray(on: boolean) {
    this.xrayButton.setAttribute('aria-pressed', String(on));
    document.documentElement.classList.toggle('is-xray', on);
  }

  setExplode(value: number) {
    this.slider.value = String(value);
  }

  setIsolated(floorKey: string | null) {
    for (const [key, button] of this.floorButtons) {
      button.setAttribute('aria-pressed', String(key === floorKey));
    }
  }

  /**
   * The timeline's position, and the heading with it: while the building is
   * going up the heading names the stage, as the demo's does; once finished
   * it is the property again.
   */
  setBuild(value: number, playing: boolean) {
    this.timeline.value = String(Math.round(value * 100));
    this.timeline.style.setProperty('--fill', `${(value / BUILD_MAX) * 100}%`);
    this.percent.textContent = `${Math.round((value / BUILD_MAX) * 100)}%`;
    this.playButton.classList.toggle('is-playing', playing);
    this.playButton.setAttribute('aria-label', playing ? 'إيقاف مؤقت' : 'تشغيل مراحل البناء');
    const index = Math.min(STAGES.length - 1, Math.floor(Math.max(0, value - 0.001)));
    this.stageMarks.forEach((m, i) => m.classList.toggle('is-current', i === index));

    const finished = value >= BUILD_MAX - 0.02;
    const stage = STAGES[index]!;
    document.documentElement.classList.toggle('is-building', !finished);
    if (finished) {
      this.heading.eyebrow.textContent = 'الخبر · مجمع مطل البحر';
      this.heading.title.textContent = 'مطل البحر';
      this.heading.lede.textContent = this.lede;
      this.heading.accent.hidden = false;
    } else {
      this.heading.eyebrow.textContent = `${two(index + 1)} / ${two(STAGES.length)} · مراحل البناء`;
      this.heading.title.textContent = stage.labelAr;
      this.heading.lede.textContent = stage.noteAr;
      this.heading.accent.hidden = true;
    }
  }

  showUnit(unit: SnapshotUnit | null) {
    if (!unit) {
      this.card.hidden = true;
      this.card.replaceChildren();
      return;
    }
    const available = unit.status === 'available';
    const row = (term: string, value: string) =>
      el('div', {}, el('dt', { textContent: term }), el('dd', { textContent: value }));

    const close = el('button', {
      type: 'button',
      className: 'close',
      ariaLabel: 'إغلاق',
      textContent: '×',
    });
    close.addEventListener('click', () => this.on.closeCard());

    const action = available
      ? el('button', { type: 'button', className: 'primary', textContent: 'استفسار' })
      : el('p', { className: 'muted', textContent: 'هذه المساحة مؤجَّرة حاليًا.' });
    if (available) action.addEventListener('click', () => this.on.enquire(unit));

    // A link straight to this space, for a prospect to send on.
    const share = el('button', {
      type: 'button',
      className: 'secondary',
      textContent: 'نسخ الرابط',
    });
    share.addEventListener('click', () => {
      const url = `${location.origin}${location.pathname}?unit=${encodeURIComponent(unit.code)}`;
      navigator.clipboard
        ?.writeText(url)
        .then(() => (share.textContent = 'نُسخ الرابط'))
        .catch(() => (share.textContent = url));
    });

    this.card.replaceChildren(
      close,
      el(
        'p',
        { className: `status ${available ? 'is-available' : 'is-leased'}` },
        el('i'),
        available ? 'متاح' : 'مؤجَّر',
      ),
      el('h2', { textContent: unit.code, dir: 'ltr' }),
      ...(unit.brand ? [el('p', { className: 'brand', textContent: unit.brand, dir: 'ltr' })] : []),
      el(
        'dl',
        {},
        row('الدور', this.floorLabels.get(unit.floor) ?? unit.floor),
        row('النوع', KIND_AR[unit.kind] ?? unit.kind),
        row('المساحة المطبوعة', unit.areaSqm === null ? 'غير مطبوعة' : sqm(unit.areaSqm)),
        row('التراس', terraceText(unit)),
      ),
      el('div', { className: 'actions' }, action, share),
    );
    this.card.hidden = false;
  }
}
